import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useWorkflows } from '@/api/hooks/useWorkflow'
import { useWorkOrders } from '@/api/hooks/useWorkOrders'
import KanbanHeader from '@/components/molecules/kanban-header'
import { PunchClockBanner } from '@/components/molecules/punch-clock-banner'
import { WorkOrderCard } from '@/components/molecules/work-order-card/work-order-card'
import { WorkOrderSearch } from '@/components/molecules/work-order-search'
import type { KanbanStatusDefinition } from '@/components/organisms/kanban-board'
import { KanbanBoard } from '@/components/organisms/kanban-board'
import { usePageHeaderActions } from '@/contexts/page-header-actions-context'
import { useKanbanFilters } from '@/hooks/useKanbanFilters'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { SettingsActions } from '@/redux/reducers/settings'
import { PermissionsUtils } from '@/utils/permissions.utils'

export const Route = createFileRoute('/_authenticated/workshop')({
  component: WorkshopPage,
})

// ─── Domain types ────────────────────────────────────────────────────────────

type Priority = 'low' | 'medium' | 'high'

// ─── Priority config ──────────────────────────────────────────────────────────

export const priorityConfig: Record<Priority, { label: string; className: string }> = {
  low: { label: 'Low', className: 'bg-layer-3 text-text-secondary border-transparent' },
  medium: { label: 'Medium', className: 'bg-warning/10 text-warning border-transparent' },
  high: { label: 'High', className: 'bg-destructive/10 text-destructive border-transparent' },
}

function WorkshopPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const layout = useAppSelector(state => state.settings.kanbanLayouts.workshop)
  const activeWorkshopId = useAppSelector(state => state.settings.activeWorkshopId)
  const setLayout = (l: 'horizontal' | 'vertical') =>
    dispatch(SettingsActions.setKanbanLayout({ page: 'workshop', layout: l }))
  const { actionsEl, setFixedHeight } = usePageHeaderActions()

  useEffect(() => {
    setFixedHeight(layout === 'horizontal')
  }, [layout, setFixedHeight])
  const { data: workOrders, isLoading: isLoadingWorkOrders } = useWorkOrders(activeWorkshopId)
  const { data: workflows } = useWorkflows(activeWorkshopId)

  const workOrderStatuses: KanbanStatusDefinition<string>[] = useMemo(() => {
    if (!workflows) return []

    return workflows?.workflowStates.map(state => ({
      id: state.id,
      title: state.name,
      workflowState: state,
    }))
  }, [workflows])

  const filteredWorkOrderStatuses = useMemo(() => {
    if (!workOrderStatuses) return []

    return workOrderStatuses.filter(status => workflows?.workshop.includes(status.id))
  }, [workOrderStatuses])

  const filteredWorkOrders = useMemo(() => {
    if (!workOrders || !workflows) return []

    return workOrders.filter(wo => wo.currentStates.some(s => workflows.workshop.includes(s.id)))
  }, [workOrders, workflows])

  const {
    statuses: visibleStatuses,
    workOrders: visibleWorkOrders,
    getItemStatuses,
  } = useKanbanFilters({
    workflowKey: 'workshop',
    statuses: filteredWorkOrderStatuses,
    workOrders: filteredWorkOrders,
  })

  return (
    <div className="flex flex-col bg-background h-full w-full">
      {actionsEl &&
        createPortal(
          <KanbanHeader
            layout={layout}
            onLayoutChange={setLayout}
            workflowKey="workshop"
            statuses={filteredWorkOrderStatuses}
            right={<WorkOrderSearch page="workshop" />}
            left={
              PermissionsUtils.hasPermission('time-entries:create') && (
                <div className="flex max-md:hidden">
                  <PunchClockBanner />
                </div>
              )
            }
          />,
          actionsEl,
        )}
      {PermissionsUtils.hasPermission('time-entries:create') && (
        <div className="px-4 py-2 border-b bg-layer-1 border-border-subtle-1 hidden max-md:flex flex-row-reverse">
          <PunchClockBanner />
        </div>
      )}
      <KanbanBoard
        boardClassName={layout === 'horizontal' ? '' : undefined}
        getItemId={item => item.id}
        getItemStatuses={getItemStatuses}
        items={visibleWorkOrders}
        layout={layout}
        renderCard={({ item, status, occurrence, layout }) => (
          <WorkOrderCard
            layout={layout}
            currentStateId={status.id}
            lane={item.currentStates.filter(s => s.id === status.id)[occurrence]?.lane ?? 'main'}
            statuses={workOrderStatuses}
            workOrder={item}
            onCardClick={wo =>
              navigate({
                to: '/workshop/workorder/$workOrderId',
                params: { workOrderId: wo.id },
                state: { initialWorkOrder: wo } as never,
              })
            }
          />
        )}
        statuses={visibleStatuses}
        isLoading={isLoadingWorkOrders}
      />
      <Outlet />
    </div>
  )
}
